import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Package, Shirt, User, Clock, Loader2, Filter, Eye, ArrowUpDown } from 'lucide-react';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import { useAuth } from '@/contexts/AuthContext';
import DetalleSolicitudModal from '@/components/solicitudes/DetalleSolicitudModal';
import EliminarSolicitudModal from '@/components/solicitudes/EliminarSolicitudModal';

const ITEMS_PER_PAGE = 10;

const SolicitudesDelDiaPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('lenceria');
  
  // Estados para modal de eliminar
  const [solicitudToDelete, setSolicitudToDelete] = useState(null);
  const [isEliminarModalOpen, setIsEliminarModalOpen] = useState(false);
  const [tipoSolicitudEliminar, setTipoSolicitudEliminar] = useState('lenceria');
  const [isLoadingInsumos, setIsLoadingInsumos] = useState(false);
  const [isLoadingLenceria, setIsLoadingLenceria] = useState(false);
  const [isLoadingLenceriaVertical, setIsLoadingLenceriaVertical] = useState(false);

  // Estados para filtros
  const today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState({
    fecha: today,
    search: '',
    estado: '',
    ordenarPor: 'createdAt' // 'createdAt' o 'fechaEntrega'
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Estados para modal de detalle
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);
  const [tipoSolicitudDetalle, setTipoSolicitudDetalle] = useState('insumos');

  // Estados para solicitudes de insumos
  const [solicitudesInsumos, setSolicitudesInsumos] = useState([]);
  const [currentPageInsumos, setCurrentPageInsumos] = useState(1);
  const [totalItemsInsumos, setTotalItemsInsumos] = useState(0);

  // Estados para solicitudes de lencería
  const [solicitudesLenceria, setSolicitudesLenceria] = useState([]);
  const [currentPageLenceria, setCurrentPageLenceria] = useState(1);
  const [totalItemsLenceria, setTotalItemsLenceria] = useState(0);

  // Estados para solicitudes de lencería vertical
  const [solicitudesLenceriaVertical, setSolicitudesLenceriaVertical] = useState([]);
  const [currentPageLenceriaVertical, setCurrentPageLenceriaVertical] = useState(1);
  const [totalItemsLenceriaVertical, setTotalItemsLenceriaVertical] = useState(0);

  // Columnas para tabla de insumos
  const columnsInsumos = [
    {
      id: 'createdAt',
      label: 'fecha de solicitud',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    {
      id: 'fechaEntrega',
      label: 'fecha de entrega',
      sortable: true,
      render: (row) => new Date(row.fechaEntrega).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    },
    {
      id: 'edificio',
      label: 'Portafolio / Propiedad',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium">{row.edificio?.nombre}</p>
          {row.hospedaje && (
            <p className="text-xs text-blue-600 font-medium">📍 {row.hospedaje.nombre}</p>
          )}
          {row.nombrePropiedad && !row.hospedaje && (
            <p className="text-xs text-blue-600 font-medium">📍 {row.nombrePropiedad}</p>
          )}
          <p className="text-xs text-gray-500">{row.edificio?.ciudad}</p>
          {row?.isExtraRuta && (
            <p className="text-xs text-red-600 font-medium">📍 Extra ruta</p>
          )}
        </div>
      )
    },
    {
      id: 'creator',
      label: 'Solicitante',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <span>{row.creator?.nombre || 'N/A'}</span>
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          {row.total_solicitado} unidades
        </Badge>
      )
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.items?.length || 0} tipo(s)</span>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'Alistado': 'bg-blue-100 text-blue-800',
          'En Camino': 'bg-blue-100 text-orange-800',
          'Entregado': 'bg-green-100 text-green-800',
          'Cancelado': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-insumos',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-blue-600 hover:bg-blue-50'
        },
        {
          key: 'eliminar-solicitud-insumos',
          icon: 'Trash',
          tooltip: 'Eliminar Solicitud',
          className: 'text-red-600 hover:bg-red-50'
        }
      ]
    }
  ];

  // Columnas para tabla de lencería
  const columnsLenceria = [
    {
      id: 'createdAt',
      label: 'fecha de solicitud',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    {
      id: 'fechaEntrega',
      label: 'fecha de entrega',
      sortable: true,
      render: (row) => new Date(row.fechaEntrega).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    },
    {
      id: 'edificio',
      label: 'Portafolio / Propiedad',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium">{row.edificio?.nombre || row.hospedaje?.edificio?.nombre}</p>
          {row.hospedaje && (
            <p className="text-xs text-purple-600 font-medium">🏠 {row.hospedaje.nombre}</p>
          )}
          {row.nombrePropiedad && !row.hospedaje && (
            <p className="text-xs text-purple-600 font-medium">🏠 {row.nombrePropiedad}</p>
          )}
          <p className="text-xs text-gray-500">{row.edificio?.ciudad || row.hospedaje?.edificio?.ciudad}</p>
          {row?.isExtraRuta && (
            <p className="text-xs"> 🚨 Extra ruta</p>
          )}
        </div>
      )
    },
    {
      id: 'creator',
      label: 'Solicitante',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <span>{row.creator?.nombre || 'N/A'}</span>
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-purple-50 text-purple-700">
          {row.total_solicitado} unidades
        </Badge>
      )
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.items?.length || 0} tipo(s)</span>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'Alistado': 'bg-blue-100 text-blue-800',
          'En Camino': 'bg-orange-100 text-orange-800',
          'Entregado': 'bg-green-100 text-green-800',
          'Cancelado': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-lenceria',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-purple-600 hover:bg-purple-50'
        },
        {
          key: 'eliminar-solicitud-lenceria',
          icon: 'Trash',
          tooltip: 'Eliminar Solicitud',
          className: 'text-red-600 hover:bg-red-50'
        }
      ]
    }
  ];

  // Columnas para tabla de lencería vertical
  const columnsLenceriaVertical = [
    {
      id: 'createdAt',
      label: 'fecha de solicitud',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    {
      id: 'fechaEntrega',
      label: 'fecha de entrega',
      sortable: true,
      render: (row) => new Date(row.fechaEntrega).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    },
    {
      id: 'edificio',
      label: 'Portafolio / Propiedad',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium">{row.edificio?.nombre}</p>
          {row.hospedaje && (
            <p className="text-xs text-green-600 font-medium">🏗️ {row.hospedaje.nombre}</p>
          )}
          {row.nombrePropiedad && !row.hospedaje && (
            <p className="text-xs text-green-600 font-medium">🏗️ {row.nombrePropiedad}</p>
          )}
          <p className="text-xs text-gray-500">{row.edificio?.ciudad}</p>
        </div>
      )
    },
    {
      id: 'creator',
      label: 'Solicitante',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <span>{row.creator?.nombre || 'N/A'}</span>
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-green-50 text-green-700">
          {row.total_solicitado} unidades
        </Badge>
      )
    },
    {
      id: 'peso_total',
      label: 'Peso',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-gray-600">{row.peso_total?.toFixed(2) || 0} kg</span>
      )
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.items?.length || 0} tipo(s)</span>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'Alistado': 'bg-blue-100 text-blue-800',
          'En Camino': 'bg-orange-100 text-orange-800',
          'Entregado': 'bg-green-100 text-green-800',
          'Cancelado': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-lenceria-vertical',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-green-600 hover:bg-green-50'
        },
        {
          key: 'eliminar-solicitud-vertical',
          icon: 'Trash',
          tooltip: 'Eliminar Solicitud',
          className: 'text-red-600 hover:bg-red-50'
        }
      ]
    }
  ];

  // Definir campos de filtro para FilterDrawer
  const filterFields = [
    {
      id: 'fecha',
      label: 'Fecha',
      type: 'date',
      placeholder: 'Seleccionar fecha'
    },
    {
      id: 'search',
      label: 'Buscar',
      type: 'text',
      placeholder: 'Edificio, propiedad, ciudad, solicitante...'
    },
    {
      id: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: '', label: 'Todos los estados' },
        { value: 'Pendiente', label: 'Pendiente' },
        { value: 'Alistado', label: 'Alistado' },
        { value: 'En Camino', label: 'En Camino' },
        { value: 'Entregado', label: 'Entregado' },
        { value: 'Cancelado', label: 'Cancelado' }
      ]
    },
    {
      id: 'ordenarPor',
      label: 'Ordenar por',
      type: 'select',
      options: [
        { value: 'createdAt', label: 'Fecha de Solicitud' },
        { value: 'fechaEntrega', label: 'Fecha de Entrega' }
      ]
    }
  ];

  // Obtener rango de fecha seleccionada
  const getDateRange = () => {
    const baseDate = new Date((filters.fecha || today) + 'T00:00:00');
    const isDefaultRange = !filters.fecha || filters.fecha === today;

    const inicioDelDia = new Date(baseDate);
    inicioDelDia.setHours(0, 0, 0, 0);

    const finDelDia = new Date(baseDate);
    finDelDia.setHours(23, 59, 59, 999);

    if (isDefaultRange) {
      inicioDelDia.setDate(inicioDelDia.getDate() - 1);
      finDelDia.setDate(finDelDia.getDate() + 5);
    }

    return { inicioDelDia, finDelDia, isDefaultRange };
  };

  const getDisplayedDateLabel = () => {
    const baseDate = new Date((filters.fecha || today) + 'T00:00:00');
    const { isDefaultRange } = getDateRange();

    if (isDefaultRange) {
      const start = new Date(baseDate);
      start.setDate(start.getDate() - 1);
      const end = new Date(baseDate);
      end.setDate(end.getDate() + 5);

      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return `Desde ${start.toLocaleDateString('es-ES', options)} hasta ${end.toLocaleDateString('es-ES', options)}`;
    }

    return baseDate.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filtrar por edificios asignados (solo para no-admin)
  const filterByEdificiosAsignados = (solicitudes) => {
    // Si es admin, puede ver todas las solicitudes
    if (user?.role === 'admin') {
      return solicitudes;
    }

    // Si no tiene edificios asignados, no ve nada
    if (!user?.edificiosAsignados || user.edificiosAsignados.length === 0) {
      return [];
    }

    // Filtrar por edificios asignados
    return solicitudes.filter(sol => {
      const edificioId = sol.edificio?.id || sol.edificio?._id;
      return user.edificiosAsignados.includes(edificioId);
    });
  };

  // Filtrar solicitudes por texto de búsqueda
  const filterBySearch = (solicitudes) => {
    if (!filters.search) return solicitudes;

    const searchLower = filters.search.toLowerCase();
    return solicitudes.filter(sol => {
      const edificio = sol.edificio?.nombre?.toLowerCase() || '';
      const hospedaje = sol.hospedaje?.nombre?.toLowerCase() || sol.nombrePropiedad?.toLowerCase() || '';
      const ciudad = sol.edificio?.ciudad?.toLowerCase() || '';
      const creator = sol.creator?.nombre?.toLowerCase() || '';

      return edificio.includes(searchLower) ||
        hospedaje.includes(searchLower) ||
        ciudad.includes(searchLower) ||
        creator.includes(searchLower);
    });
  };

  // Filtrar por estado
  const filterByEstado = (solicitudes) => {
    if (!filters.estado) return solicitudes;
    return solicitudes.filter(sol => sol.estado === filters.estado);
  };

  // Manejar aplicación de filtros
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setIsFilterDrawerOpen(false);
  };

  // Manejar eliminación de filtro individual
  const handleRemoveFilter = (filterId) => {
    setFilters(prev => ({
      ...prev,
      [filterId]: filterId === 'fecha' ? today : ''
    }));
  };

  // Contar filtros activos
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.estado) count++;
    if (filters.fecha !== today) count++;
    return count;
  };

  // Función para abrir modal de detalle
  const handleVerDetalle = (solicitud, tipo) => {
    setSelectedSolicitud(solicitud);
    setTipoSolicitudDetalle(tipo);
    setIsDetalleModalOpen(true);
  };

  // Función para cerrar modal de detalle
  const handleCloseDetalle = () => {
    setIsDetalleModalOpen(false);
    setSelectedSolicitud(null);
  };

  // Función para manejar acciones de la tabla
  const handleAction = (actionKey, row) => {
    if (actionKey === 'ver-detalle-insumos') {
      handleVerDetalle(row, 'insumos');
    } else if (actionKey === 'ver-detalle-lenceria') {
      handleVerDetalle(row, 'lenceria');
    } else if (actionKey === 'ver-detalle-lenceria-vertical') {
      handleVerDetalle(row, 'lenceria');
    } else if (actionKey === 'eliminar-solicitud-insumos') {
      handleEliminarSolicitud(row, 'insumos');
    } else if (actionKey === 'eliminar-solicitud-lenceria') {
      handleEliminarSolicitud(row, 'lenceria');
    } else if (actionKey === 'eliminar-solicitud-vertical') {
      handleEliminarSolicitud(row, 'vertical');
    }
  };

  // Función para abrir modal de eliminar
  const handleEliminarSolicitud = (solicitud, tipo) => {
    setSolicitudToDelete(solicitud);
    setTipoSolicitudEliminar(tipo);
    setIsEliminarModalOpen(true);
  };

  // Función para confirmar eliminación
  const handleConfirmEliminar = async (solicitud, motivo) => {
    try {
      // Determinar el endpoint según el tipo
      let endpoint = '';
      if (tipoSolicitudEliminar === 'insumos') {
        endpoint = `solicitud-insumos/${solicitud._id}`;
      } else if (tipoSolicitudEliminar === 'vertical') {
        endpoint = `solicitud-prendas-vertical/${solicitud._id}`;
      } else {
        endpoint = `solicitud-prendas/${solicitud._id}`;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/${endpoint}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            motivo,
            eliminadoPor: {
              id: user._id || user.id,
              nombre: user.name || user.nombre
            }
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error al eliminar la solicitud');
      }

      toast({
        title: 'Éxito',
        description: 'Solicitud eliminada correctamente',
      });

      // Recargar solicitudes según el tipo
      if (tipoSolicitudEliminar === 'insumos') {
        fetchSolicitudesInsumos();
      } else if (tipoSolicitudEliminar === 'vertical') {
        fetchSolicitudesLenceriaVertical();
      } else {
        fetchSolicitudesLenceria();
      }
      
      // Cerrar modal
      setIsEliminarModalOpen(false);
      setSolicitudToDelete(null);
    } catch (error) {
      console.error('Error al eliminar solicitud:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar la solicitud',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Fetch solicitudes de insumos
  const fetchSolicitudesInsumos = useCallback(async () => {
    setIsLoadingInsumos(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-insumos/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: 1000,
          offset: 1,
          query: {
            fechaEntrega: {
              $gte: inicioDelDia.toISOString(),
              $lte: finDelDia.toISOString()
            },
            estado: { $ne: 'Eliminado' } // Excluir eliminadas en el backend
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        let filteredData = data.data || [];
        
        filteredData = filterByEdificiosAsignados(filteredData);
        filteredData = filterBySearch(filteredData);
        filteredData = filterByEstado(filteredData);

        // Ordenar según el filtro seleccionado
        filteredData.sort((a, b) => {
          const dateA = new Date(a[filters.ordenarPor]);
          const dateB = new Date(b[filters.ordenarPor]);
          return dateB - dateA; // Orden descendente (más reciente primero)
        });

        // Aplicar paginación manual
        const startIndex = (currentPageInsumos - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        setSolicitudesInsumos(paginatedData);
        setTotalItemsInsumos(filteredData.length);
      } else {
        throw new Error(data.message || 'Error al cargar solicitudes de insumos');
      }
    } catch (error) {
      console.error('Error al cargar solicitudes de insumos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las solicitudes de insumos",
        variant: "destructive",
      });
    } finally {
      setIsLoadingInsumos(false);
    }
  }, [currentPageInsumos, filters, toast, user]);

  // Fetch solicitudes de lencería
  const fetchSolicitudesLenceria = useCallback(async () => {
    setIsLoadingLenceria(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: 1000,
          offset: 1,
          query: {
            fechaEntrega: {
              $gte: inicioDelDia.toISOString(),
              $lte: finDelDia.toISOString()
            },
            estado: { $ne: 'Eliminado' } // Excluir eliminadas en el backend
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        let filteredData = data.data || [];
        
        filteredData = filterByEdificiosAsignados(filteredData);
        filteredData = filterBySearch(filteredData);
        filteredData = filterByEstado(filteredData);

        // Ordenar según el filtro seleccionado
        filteredData.sort((a, b) => {
          const dateA = new Date(a[filters.ordenarPor]);
          const dateB = new Date(b[filters.ordenarPor]);
          return dateB - dateA; // Orden descendente (más reciente primero)
        });

        // Aplicar paginación manual
        const startIndex = (currentPageLenceria - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        setSolicitudesLenceria(paginatedData);
        setTotalItemsLenceria(filteredData.length);
      } else {
        throw new Error(data.message || 'Error al cargar solicitudes de lencería');
      }
    } catch (error) {
      console.error('Error al cargar solicitudes de lencería:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las solicitudes de lencería",
        variant: "destructive",
      });
    } finally {
      setIsLoadingLenceria(false);
    }
  }, [currentPageLenceria, filters, toast, user]);

  // Fetch solicitudes de lencería vertical
  const fetchSolicitudesLenceriaVertical = useCallback(async () => {
    setIsLoadingLenceriaVertical(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas-vertical/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: 1000,
          offset: 1,
          query: {
            fechaEntrega: {
              $gte: inicioDelDia.toISOString(),
              $lte: finDelDia.toISOString()
            },
            estado: { $ne: 'Eliminado' } // Excluir eliminadas en el backend
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        let filteredData = data.data || [];
        
        filteredData = filterByEdificiosAsignados(filteredData);
        filteredData = filterBySearch(filteredData);
        filteredData = filterByEstado(filteredData);

        // Ordenar según el filtro seleccionado
        filteredData.sort((a, b) => {
          const dateA = new Date(a[filters.ordenarPor]);
          const dateB = new Date(b[filters.ordenarPor]);
          return dateB - dateA; // Orden descendente (más reciente primero)
        });

        // Aplicar paginación manual
        const startIndex = (currentPageLenceriaVertical - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        setSolicitudesLenceriaVertical(paginatedData);
        setTotalItemsLenceriaVertical(filteredData.length);
      } else {
        throw new Error(data.message || 'Error al cargar solicitudes de lencería vertical');
      }
    } catch (error) {
      console.error('Error al cargar solicitudes de lencería vertical:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las solicitudes de lencería vertical",
        variant: "destructive",
      });
    } finally {
      setIsLoadingLenceriaVertical(false);
    }
  }, [currentPageLenceriaVertical, filters, toast, user]);

  // Cargar todos los tipos de solicitudes al iniciar y cuando cambien los filtros
  useEffect(() => {
    fetchSolicitudesInsumos();
    fetchSolicitudesLenceria();
    fetchSolicitudesLenceriaVertical();
  }, [fetchSolicitudesInsumos, fetchSolicitudesLenceria, fetchSolicitudesLenceriaVertical, currentPageInsumos, currentPageLenceria, currentPageLenceriaVertical, filters.ordenarPor]);

  // Calcular estadísticas
  const getEstadisticas = () => {
    const totalInsumos = totalItemsInsumos;
    const totalLenceria = totalItemsLenceria;
    const totalLenceriaVertical = totalItemsLenceriaVertical;
    const totalGeneral = totalInsumos + totalLenceria + totalLenceriaVertical;

    return {
      totalInsumos,
      totalLenceria,
      totalLenceriaVertical,
      totalGeneral
    };
  };

  const stats = getEstadisticas();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Calendar className="h-8 w-8 text-primary" />
            Solicitudes
          </h1>
          <p className="text-muted-foreground mt-1">
            {getDisplayedDateLabel()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Selector de ordenamiento */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <Select
              value={filters.ordenarPor}
              onValueChange={(value) => setFilters(prev => ({ ...prev, ordenarPor: value }))}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">Fecha de Solicitud</SelectItem>
                <SelectItem value="fechaEntrega">Fecha de Entrega</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsFilterDrawerOpen(true)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filtros
            {getActiveFiltersCount() > 0 && (
              <Badge variant="secondary" className="ml-1">
                {getActiveFiltersCount()}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Filtros Aplicados */}
      {getActiveFiltersCount() > 0 && (
        <AppliedFilters
          filters={filters}
          fields={filterFields}
          onRemoveFilter={handleRemoveFilter}
        />
      )}

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Solicitudes</p>
                <p className="text-3xl font-bold text-primary">{stats.totalGeneral}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Insumos</p>
                <p className="text-3xl font-bold text-blue-600">{stats.totalInsumos}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Lencería - Horizontal</p>
                <p className="text-3xl font-bold text-purple-600">{stats.totalLenceria}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Shirt className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Lencería - Vertical</p>
                <p className="text-3xl font-bold text-green-600">{stats.totalLenceriaVertical}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Shirt className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de solicitudes */}
      <Card>
        <CardHeader>
          <CardTitle>Solicitudes Registradas</CardTitle>
          <CardDescription>
            Vista consolidada de todas las solicitudes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-6 bg-gray-100 p-1 rounded-lg h-auto gap-1">
              <TabsTrigger 
                value="lenceria" 
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-purple-700 data-[state=active]:font-semibold data-[state=active]:border-purple-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Shirt className="h-4 w-4" />
                <span className="hidden sm:inline">Lencería - horizontal</span>
                <Badge variant="secondary" className="ml-1 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                  {stats.totalLenceria}
                </Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="lenceria-vertical" 
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-green-700 data-[state=active]:font-semibold data-[state=active]:border-green-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Shirt className="h-4 w-4" />
                <span className="hidden sm:inline">Lencería - vertical</span>
                <Badge variant="secondary" className="ml-1 data-[state=active]:bg-green-100 data-[state=active]:text-green-700">
                  {stats.totalLenceriaVertical}
                </Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="insumos" 
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-700 data-[state=active]:font-semibold data-[state=active]:border-blue-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">Insumos</span>
                <Badge variant="secondary" className="ml-1 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
                  {stats.totalInsumos}
                </Badge>
              </TabsTrigger>
            </TabsList>

            {/* Tab de Insumos */}
            <TabsContent value="insumos" className="space-y-4">
              {isLoadingInsumos ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : solicitudesInsumos.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    No hay solicitudes de insumos hoy
                  </p>
                  <p className="text-gray-500">
                    Las solicitudes aparecerán aquí cuando se registren
                  </p>
                </div>
              ) : (
                <>
                  <DataTable
                    columns={columnsInsumos}
                    data={solicitudesInsumos}
                    onAction={handleAction}
                  />
                  <Pagination
                    currentPage={currentPageInsumos}
                    totalItems={totalItemsInsumos}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPageInsumos}
                  />
                </>
              )}
            </TabsContent>

            {/* Tab de Lencería */}
            <TabsContent value="lenceria" className="space-y-4">
              {isLoadingLenceria ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : solicitudesLenceria.length === 0 ? (
                <div className="text-center py-12">
                  <Shirt className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    No hay solicitudes de lencería hoy
                  </p>
                  <p className="text-gray-500">
                    Las solicitudes aparecerán aquí cuando se registren
                  </p>
                </div>
              ) : (
                <>
                  <DataTable
                    columns={columnsLenceria}
                    data={solicitudesLenceria}
                    onAction={handleAction}
                  />
                  <Pagination
                    currentPage={currentPageLenceria}
                    totalItems={totalItemsLenceria}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPageLenceria}
                  />
                </>
              )}
            </TabsContent>

            {/* Tab de Lencería Vertical */}
            <TabsContent value="lenceria-vertical" className="space-y-4">
              {isLoadingLenceriaVertical ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : solicitudesLenceriaVertical.length === 0 ? (
                <div className="text-center py-12">
                  <Shirt className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    No hay solicitudes de lencería vertical hoy
                  </p>
                  <p className="text-gray-500">
                    Las solicitudes aparecerán aquí cuando se registren
                  </p>
                </div>
              ) : (
                <>
                  <DataTable
                    columns={columnsLenceriaVertical}
                    data={solicitudesLenceriaVertical}
                    onAction={handleAction}
                  />
                  <Pagination
                    currentPage={currentPageLenceriaVertical}
                    totalItems={totalItemsLenceriaVertical}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPageLenceriaVertical}
                  />
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onApplyFilters={handleApplyFilters}
        initialDay={true}
      />

      {/* Modal de Detalle de Solicitud */}
      <DetalleSolicitudModal
        isOpen={isDetalleModalOpen}
        onClose={handleCloseDetalle}
        solicitud={selectedSolicitud}
        tipo={tipoSolicitudDetalle}
      />

      {/* Modal de Eliminar Solicitud */}
      <EliminarSolicitudModal
        isOpen={isEliminarModalOpen}
        onClose={() => {
          setIsEliminarModalOpen(false);
          setSolicitudToDelete(null);
        }}
        solicitud={solicitudToDelete}
        onConfirm={handleConfirmEliminar}
        tipo={tipoSolicitudEliminar}
      />
    </motion.div>
  );
};

export default SolicitudesDelDiaPage;
